export const DEFAULT_RESULT_IMAGE = "/img/cover/cover_resultat.webp";

export const DEFAULT_RESULT_FORM = {
  resultats: "",
  classement: "",
  observations: "",
  podium_url: "",
  tableau_url: "",
};

export const DEFAULT_RESULTATS_FILTERS = {
  search: "",
  status: "all",
  palmares: "all",
  sort: "dateDesc",
};

export const RESULT_RANK_OPTIONS = [
  "1èr(e)",
  "2ème",
  "3ème",
  "4ème",
  "5ème",
  "7ème",
  "9ème",
  "N.C.",
];

export const RESULT_TOKENS = [
  {
    value: "✅",
    label: "Gagné",
    className:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
  },
  {
    value: "❌",
    label: "Perdu",
    className: "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200",
  },
  {
    value: "N",
    label: "Nul",
    className:
      "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70",
  },
  {
    value: "F",
    label: "Forfait",
    className:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200",
  },
  {
    value: "D",
    label: "Disqualifié",
    className:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200",
  },
];

export const normalizeAdherentsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.adherents)) return response.data.adherents;
  return [];
};

export const normalizeEventsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Events)) return response.Events;
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.Events)) return response.data.Events;
  if (Array.isArray(response?.data?.events)) return response.data.events;
  return [];
};

export const extractPalmaresRecord = (response) => {
  if (!response) return null;
  if (response?.palmares) return response.palmares;
  if (response?.data?.palmares) return response.data.palmares;
  if (response?.data?.id) return response.data;
  if (response?.id) return response;
  if (response?.palmaresId) return { id: response.palmaresId };
  return null;
};

export const getPalmaresRecordId = (record) =>
  record?.id || record?.palmares_id || record?.palmaresId || null;

export function safeDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function getAdherentEventIds(event) {
  return {
    adherentId:
      event?.AdherentEvent?.adherent_id ||
      event?.adherent_id ||
      event?.Adherent?.id ||
      null,
    eventId:
      event?.AdherentEvent?.event_id || event?.event_id || event?.id || null,
  };
}

export function getResultEventKey(event, index = "") {
  const { adherentId, eventId } = getAdherentEventIds(event);

  if (adherentId && eventId) {
    return `result-${adherentId}-${eventId}`;
  }

  if (event?.id) {
    return `result-event-${event.id}-${index}`;
  }

  return `result-fallback-${index}`;
}

export function getPalmaresStatusKey(event) {
  const { adherentId, eventId } = getAdherentEventIds(event);

  if (adherentId && eventId) {
    return `${adherentId}-${eventId}`;
  }

  return getResultEventKey(event);
}

export function dedupeResultEvents(events = []) {
  const seen = new Set();

  return events.filter((event, index) => {
    const key = getResultEventKey(event, index);

    if (seen.has(key)) {
      console.warn("[ResultatsPage] Événement dupliqué ignoré :", key, event);
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function formatResultDate(value) {
  const date = safeDate(value);
  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatResultShortDate(value) {
  const date = safeDate(value);
  if (!date) return "--/--/----";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function formatResultDay(value) {
  const date = safeDate(value);
  if (!date) return "--";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
  }).format(date);
}

export function formatResultMonth(value) {
  const date = safeDate(value);
  if (!date) return "---";

  return new Intl.DateTimeFormat("fr-FR", {
    month: "short",
  })
    .format(date)
    .replace(".", "");
}

export function formatResultYear(value) {
  const date = safeDate(value);
  if (!date) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
  }).format(date);
}

export function getResultValue(event, field, fallback = "Non spécifié") {
  return event?.AdherentEvent?.[field] || fallback;
}

export function hasResult(event) {
  return Boolean(
    event?.AdherentEvent?.resultats ||
    event?.AdherentEvent?.classement ||
    event?.AdherentEvent?.observations,
  );
}

export function hasDocuments(event) {
  return Boolean(
    event?.AdherentEvent?.podium_url || event?.AdherentEvent?.tableau_url,
  );
}

export function hasPodiumResult(event) {
  const classement = normalizeText(event?.AdherentEvent?.classement);

  return (
    classement.includes("1er") ||
    classement.includes("1ere") ||
    classement.includes("1er(e)") ||
    classement.includes("2eme") ||
    classement.includes("3eme")
  );
}

export function parseResultTokens(resultats = "") {
  return String(resultats || "")
    .split("")
    .filter(Boolean);
}

export function getResultTokenLabel(token) {
  const match = RESULT_TOKENS.find((item) => item.value === token);
  return match?.label || token;
}

export function getResultTokenClass(token) {
  const match = RESULT_TOKENS.find((item) => item.value === token);

  return (
    match?.className ||
    "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70"
  );
}

export function sortResultEvents(events = [], sort = "dateDesc") {
  const sortedEvents = [...events];

  if (sort === "dateAsc") {
    return sortedEvents.sort((a, b) => {
      const dateA = safeDate(a?.start)?.getTime() || 0;
      const dateB = safeDate(b?.start)?.getTime() || 0;
      return dateA - dateB;
    });
  }

  if (sort === "titleAsc") {
    return sortedEvents.sort((a, b) =>
      String(a?.titre || "").localeCompare(String(b?.titre || ""), "fr"),
    );
  }

  if (sort === "withResultsFirst") {
    return sortedEvents.sort(
      (a, b) => Number(hasResult(b)) - Number(hasResult(a)),
    );
  }

  return sortedEvents.sort((a, b) => {
    const dateA = safeDate(a?.start)?.getTime() || 0;
    const dateB = safeDate(b?.start)?.getTime() || 0;
    return dateB - dateA;
  });
}

export function filterResultEvents(
  events = [],
  filters = DEFAULT_RESULTATS_FILTERS,
  palmaresStatus = {},
) {
  const search = normalizeText(filters.search);

  const filtered = events.filter((event) => {
    const statusKey = getPalmaresStatusKey(event);
    const isInPalmares = Boolean(palmaresStatus[statusKey]);

    const searchableContent = normalizeText(
      [
        event?.titre,
        event?.description,
        event?.lieu_event,
        event?.type_event,
        event?.level_event,
        event?.AdherentEvent?.resultats,
        event?.AdherentEvent?.classement,
        event?.AdherentEvent?.observations,
      ].join(" "),
    );

    const matchesSearch = !search || searchableContent.includes(search);

    const matchesStatus =
      filters.status === "all" ||
      (filters.status === "withResults" && hasResult(event)) ||
      (filters.status === "withoutResults" && !hasResult(event)) ||
      (filters.status === "withDocuments" && hasDocuments(event)) ||
      (filters.status === "podium" && hasPodiumResult(event));

    const matchesPalmares =
      filters.palmares === "all" ||
      (filters.palmares === "yes" && isInPalmares) ||
      (filters.palmares === "no" && !isInPalmares);

    return matchesSearch && matchesStatus && matchesPalmares;
  });

  return sortResultEvents(filtered, filters.sort);
}

export function countActiveResultFilters(filters = DEFAULT_RESULTATS_FILTERS) {
  return Object.entries(filters).reduce((count, [key, value]) => {
    if (key === "status" && value === "all") return count;
    if (key === "palmares" && value === "all") return count;
    if (key === "sort" && value === "dateDesc") return count;
    if (!value) return count;

    return count + 1;
  }, 0);
}

export function getResultatsStats(events = [], palmaresStatus = {}) {
  const withResults = events.filter(hasResult).length;
  const withoutResults = Math.max(events.length - withResults, 0);
  const withDocuments = events.filter(hasDocuments).length;
  const podiums = events.filter(hasPodiumResult).length;

  const inPalmares = events.filter((event) =>
    Boolean(palmaresStatus[getPalmaresStatusKey(event)]),
  ).length;

  return {
    total: events.length,
    withResults,
    withoutResults,
    withDocuments,
    podiums,
    inPalmares,
  };
}
